import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface FormParameterValues {
  [key: string]: string | number | boolean;
}

export interface FormParameterError {
  parameter: string;
  message: string;
}

export interface UseFormParametersResult {
  parameters: FormParameterValues;
  errors: FormParameterError[];
  isValid: boolean;
  missingRequired: string[];
}

/**
 * Hook to validate and extract required parameters from URL query parameters
 * based on form definition requirements
 */
export function useFormParameters(
  requiredParameters: FormParameter[] = [],
): UseFormParametersResult {
  const [searchParams] = useSearchParams();

  const result = useMemo(() => {
    const parameters: FormParameterValues = {};
    const errors: FormParameterError[] = [];
    const missingRequired: string[] = [];

    for (const paramDef of requiredParameters) {
      // Get value from query parameters only
      const rawValue = searchParams.get(paramDef.key);
      const sourceValue = rawValue;

      // Check if required parameter is missing
      if (paramDef.required && !sourceValue) {
        if (paramDef.defaultValue !== undefined) {
          parameters[paramDef.key] = paramDef.defaultValue;
        } else {
          missingRequired.push(paramDef.key);
          errors.push({
            parameter: paramDef.key,
            message: `Required parameter '${paramDef.key}' is missing. ${paramDef.description}`,
          });
          continue;
        }
      }

      // Use default value if parameter is not provided
      if (!sourceValue && paramDef.defaultValue !== undefined) {
        parameters[paramDef.key] = paramDef.defaultValue;
        continue;
      }

      // Skip optional parameters that aren't provided
      if (!sourceValue && !paramDef.required) {
        continue;
      }

      // Type conversion and validation (only if we have a source value)
      if (sourceValue) {
        try {
          let parsedValue: string | number | boolean;

          switch (paramDef.type) {
            case 'string':
              parsedValue = sourceValue;
              break;
            case 'number':
              parsedValue = Number(sourceValue);
              if (isNaN(parsedValue)) {
                errors.push({
                  parameter: paramDef.key,
                  message: `Parameter '${paramDef.key}' must be a valid number, got '${sourceValue}'`,
                });
                continue;
              }
              break;
            case 'boolean':
              if (sourceValue === 'true' || sourceValue === '1') {
                parsedValue = true;
              } else if (sourceValue === 'false' || sourceValue === '0') {
                parsedValue = false;
              } else {
                errors.push({
                  parameter: paramDef.key,
                  message: `Parameter '${paramDef.key}' must be 'true', 'false', '1', or '0', got '${sourceValue}'`,
                });
                continue;
              }
              break;
            default:
              parsedValue = sourceValue;
          }

          parameters[paramDef.key] = parsedValue;
        } catch (error) {
          errors.push({
            parameter: paramDef.key,
            message: `Failed to parse parameter '${paramDef.key}': ${error}`,
          });
        }
      }
    }

    return {
      parameters,
      errors,
      isValid: errors.length === 0,
      missingRequired,
    };
  }, [searchParams, requiredParameters]);

  return result;
}

const parameterFallbacks: Record<string, string> = {
  clientPartnerFullName: 'spouse',
};

/**
 * Utility function to interpolate variables in text strings
 * Variables are specified using {variable_name} syntax
 * Automatically decodes URI-encoded values for human readability
 */
export function interpolateText(text: string, parameters: FormParameterValues): string {
  return text.replace(/\{(\w+)\}/g, (match, paramName) => {
    const value = parameters[paramName];
    if (value !== undefined) {
      try {
        // Decode URI-encoded values for human readability
        const decodedValue = decodeURIComponent(String(value));
        return decodedValue;
      } catch (error) {
        // If decoding fails, return the original value
        return String(value);
      }
    }
    return parameterFallbacks[paramName] || match;
  });
}
